/**
 * Loader Component
 *
 * This component provides a customizable circular loader using HTML canvas.
 * It consists of two main components:
 *
 * 1. CircularProgress:
 *    - Draws a static circular progress ring on a canvas element.
 *    - Supports customization of radius, stroke width, and gradient colors.
 *    - Uses a linear gradient from `firstColor` to `endColor`.
 *    - Automatically updates the canvas whenever the props change.
 *
 * 2. Loader:
 *    - Wraps CircularProgress with a spinning animation using CSS `animate-spin`.
 *    - Can be used wherever a loading indicator is needed in the UI.
 *
 * Props (`CircularProgressProps`):
 * - radius (number): Radius of the circle in pixels (default: 20)
 * - strokeWidth (number): Width of the stroke (default: 1)
 * - firstColor (string): Starting color of the gradient (default: LoaderFirstColor)
 * - endColor (string): Ending color of the gradient (default: LoaderSecondColor)
 *
 * Usage:
 * <Loader radius={25} strokeWidth={2} firstColor="#f00" endColor="#0f0" />
 *
 * Notes:
 * - The component uses a canvas to draw the gradient circle, which is then
 *   animated using CSS for spinning effects.
 * - Works well for loading states in forms, buttons, or any asynchronous content.
 */

import React, { useEffect, useRef } from "react";
import { CircularProgressProps } from "../../types";
import {
  LoaderFirstColor,
  LoaderSecondColor,
} from "../../utils/constants/CommonConstants";
import CustomLottieIcon from "../custom/CustomLottieIcon";
import loadingAnimation from "../../looties/loader 2.json";


const CircularProgress: React.FC<CircularProgressProps> = ({
  radius = 20,
  strokeWidth = 1,
  firstColor = LoaderFirstColor,
  endColor = LoaderSecondColor,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Function to draw the circular progress
  const drawCircularProgress = (canvas: HTMLCanvasElement) => {
    const context = canvas.getContext("2d");
    if (!context) return;

    const x = canvas.width / 2;
    const y = canvas.height / 2;
    const offset = strokeWidth / 2;
    const radiusWithOffset = radius - offset;
    const gradient = context.createLinearGradient(0, 0, canvas.width, 0);

    // Set the gradient colors
    gradient.addColorStop(0, firstColor);
    gradient.addColorStop(1, endColor);

    // Set up the paint properties
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = gradient;
    context.lineWidth = strokeWidth;
    context.beginPath();
    context.arc(x, y, radiusWithOffset, 0, 2 * Math.PI);
    context.stroke();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      drawCircularProgress(canvas);
    }
  }, [radius, strokeWidth, firstColor, endColor]);

  return (
    <div className="relative flex items-center justify-center">
      <canvas
        ref={canvasRef}
        width={radius * 2 + strokeWidth}
        height={radius * 2 + strokeWidth}
      />
    </div>
  );
};

const Loader: React.FC<CircularProgressProps> = ({
  radius,
  strokeWidth,
  firstColor,
  endColor,
}) => {
  return (
    <div className="flex items-center justify-center">
      <CustomLottieIcon LottieAnimation={loadingAnimation} size={150} />
    </div>
  );
};

export default Loader;
